// Recibir mercancía sin señal.
//
// POR QUÉ ESTA COLA SÍ GUARDA LA PETICIÓN, y la de conteos no. La cola de
// conteos guarda TOTALES porque el endpoint de captura suma, y reenviar
// «suma una» cuenta dos. La recepción era el mismo caso —y por eso se quedó
// fuera de la cola hasta ahora—, pero ya no: el endpoint acepta una clave
// de idempotencia y reconoce el reenvío. Con esa barrera puesta, guardar la
// petición y reenviarla es correcto, y es la forma más simple.
//
// Es exactamente la misma forma que `cola.js`, y a propósito: quien lea las
// dos tiene que ver el mismo mecanismo, no dos invenciones distintas.
//
// LO QUE ESTA COLA TIENE Y LAS OTRAS NO: una recepción encolada describe
// mercancía QUE YA ESTÁ FÍSICAMENTE EN EL ANDÉN. El sistema todavía no lo
// sabe, y esa diferencia entre lo que hay y lo que el sistema cree es
// justo lo que produce que alguien reciba la misma tarima dos veces. Por
// eso lo encolado se ANUNCIA en la lista de órdenes en vez de quedarse
// callado dentro del teléfono.
import { api, ErrorApi } from '../api';

const DB = 'tego-recepciones';
const STORE = 'recepciones';

function abrir() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => {
      if (!r.result.objectStoreNames.contains(STORE)) {
        r.result.createObjectStore(STORE, { keyPath: 'clave_idempotencia' });
      }
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

function conStore(modo, fn) {
  return abrir().then((d) => new Promise((res, rej) => {
    const t = d.transaction(STORE, modo);
    const rq = fn(t.objectStore(STORE));
    t.oncomplete = () => res(rq && rq.result);
    t.onerror = () => rej(t.error);
    t.onabort = () => rej(t.error);
  }));
}

const guardar = (r) => conStore('readwrite', (s) => s.put(r));
const quitar = (clave) => conStore('readwrite', (s) => s.delete(clave));
export const pendientesRecepcion = () => conStore('readonly', (s) => s.getAll());

export async function contarPendientesRecepcion() {
  return (await pendientesRecepcion()).length;
}

/** Las órdenes con una recepción encolada: {id_orden: nº de recepciones}. */
export async function ordenesConRecepcionEnCola() {
  const cuenta = {};
  for (const r of await pendientesRecepcion()) {
    cuenta[r.id_orden] = (cuenta[r.id_orden] || 0) + 1;
  }
  return cuenta;
}

// Lo que va al backend, sin los campos internos de la cola.
function carga(r) {
  // `id_orden` va en la URL, no en el cuerpo: se descarta aquí.
  const { _ts, _error, id_orden: _orden, ...resto } = r;
  return resto;
}

// Un error de negocio no se arregla reintentando: la orden se canceló, o
// alguien la recibió entera desde otro sitio. Se saca de la cola —si no,
// atasca el reenvío para siempre— pero NO se calla (ver `sincronizar`).
function esDefinitivo(e) {
  return e instanceof ErrorApi && [400, 403, 404, 409, 422].includes(e.status);
}

/**
 * Registra una recepción. Devuelve:
 *   {estado:'aplicado', orden}   — entró al inventario de verdad
 *   {estado:'encolado'}          — sin señal; guardada en el teléfono
 * Lanza ErrorApi si el servidor la rechaza por regla de negocio.
 *
 * Se guarda ANTES de intentar enviarla, igual que en las otras dos colas:
 * si el navegador se cierra entre el envío y la respuesta, la recepción
 * sigue estando, y reenviarla no duplica nada porque lleva su clave.
 */
export async function registrarRecepcion({ id_orden, ...datos }) {
  const r = { ...datos, id_orden, _ts: Date.now() };
  await guardar(r);

  if (!navigator.onLine) return { estado: 'encolado' };
  try {
    const orden = await api.post(`/compras/ordenes/${id_orden}/recibir`, carga(r));
    await quitar(r.clave_idempotencia);
    return { estado: 'aplicado', orden };
  } catch (e) {
    if (esDefinitivo(e)) {
      await quitar(r.clave_idempotencia);
      throw e;
    }
    // Red o servidor: se queda encolada. Aquí está el caso que motivó todo
    // esto — si la petición SÍ llegó y lo que se perdió fue la respuesta, el
    // reenvío no duplica: el servidor reconoce la clave.
    return { estado: 'encolado' };
  }
}

/** Reenvía lo pendiente. Devuelve {enviados, rechazados, restantes}. */
export async function sincronizarRecepciones() {
  if (!navigator.onLine) {
    return { enviados: 0, rechazados: [], restantes: await contarPendientesRecepcion() };
  }
  let enviados = 0;
  const rechazados = [];
  for (const r of await pendientesRecepcion()) {
    try {
      await api.post(`/compras/ordenes/${r.id_orden}/recibir`, carga(r));
      await quitar(r.clave_idempotencia);
      enviados += 1;
    } catch (e) {
      if (esDefinitivo(e)) {
        await quitar(r.clave_idempotencia);
        // UNA RECEPCIÓN RECHAZADA NO SE PUEDE TRAGAR. Un movimiento
        // rechazado es trabajo que no se hizo; una recepción rechazada es
        // mercancía que YA ESTÁ EN EL ANDÉN y que el sistema no va a
        // registrar nunca si nadie se entera. Se marca como de recepción
        // para que el aviso lo diga con esas palabras.
        rechazados.push({ ...r, _tipo: 'RECEPCION', _error: e.message });
      }
      // Red o servidor: se deja para el próximo intento.
    }
  }
  return { enviados, rechazados, restantes: await contarPendientesRecepcion() };
}

// ---------------------------------------------------------------------
// Las ubicaciones, en el teléfono.
//
// Sin esto la cola no sirve de nada: el diálogo de recibir pide la lista de
// ubicaciones a la API al abrirse, y sin señal llegaría vacía — no habría
// dónde meter la mercancía y no se podría ni confirmar. Se guarda la última
// lista vista y se usa como respaldo. Va aquí, y no en un caché genérico,
// porque existe por una sola razón: que se pueda recibir sin cobertura.
// ---------------------------------------------------------------------
const CLAVE_UBICACIONES = 'tego.ubicaciones';

export function recordarUbicaciones(lista) {
  try {
    localStorage.setItem(CLAVE_UBICACIONES, JSON.stringify(lista));
  } catch { /* sin espacio: se seguirá pidiendo a la API */ }
}

export function ubicacionesRecordadas() {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_UBICACIONES) || '[]');
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}

// ---------------------------------------------------------------------
// El interruptor de calidad, en el teléfono.
//
// MISMO PROBLEMA Y MISMA SOLUCIÓN QUE LAS UBICACIONES, veinte líneas más
// arriba. El diálogo de recibir pregunta a la API si esta empresa exige
// el lote del proveedor; sin señal esa llamada falla, y caer en «no lo
// exige» sería peor que no preguntar: la recepción se encolaría sin el
// dato, y al recuperar señal el servidor la rechazaría —con razón— por
// algo que la pantalla nunca dijo.
//
// Se guarda lo último que se supo y se usa como respaldo. Ante la duda,
// la última verdad conocida gana al valor por omisión.
// ---------------------------------------------------------------------
const CLAVE_CALIDAD = 'tego.calidad.exige_lote_proveedor';

export function recordarExigenciaDeLote(exige) {
  try {
    localStorage.setItem(CLAVE_CALIDAD, exige ? '1' : '0');
  } catch { /* sin espacio: se seguirá pidiendo a la API */ }
}

export function exigenciaDeLoteRecordada() {
  try {
    return localStorage.getItem(CLAVE_CALIDAD) === '1';
  } catch { return false; }
}
